import React from 'react';

import {Pagination} from 'lib/Listings';
import {GLOBAL_SEARCH_URL} from 'search/SearchConstants';
import * as net from 'util/Net';

import {STATUS_CODE_TO_STATUS} from './ModelRunTypes';
import type {ModelRunRow, SortConfig} from './ModelRunTypes';

export interface ModelRunSearchFilters {
  query: string;
  creatorId: string | null;
  inputEntityId: string | null;
}

export const DEFAULT_FILTERS: ModelRunSearchFilters = {
  query: '',
  creatorId: null,
  inputEntityId: null,
};

interface ModelRunListingData {
  id: string;
  name: string;
  modelName: string;
  statusCode: string;
  createdAt: string;
  modifiedAt: string;
  creator?: {name: string} | null;
  folder?: {name: string} | null;
}

interface SearchResponse {
  ids: Array<string>;
  total: number;
  isExact: boolean;
  modelRunsById?: Record<string, ModelRunListingData>;
}

function buildSearchFilters(filters: ModelRunSearchFilters): Array<Record<string, unknown>> {
  const searchFilters: Array<Record<string, unknown>> = [
    {key: 'TYPES', operator: 'IS_ONE_OF', value: ['model_run']},
  ];

  if (filters.creatorId) {
    searchFilters.push({
      key: 'CREATOR',
      operator: 'IS_ONE_OF',
      value: [filters.creatorId],
    });
  }

  if (filters.inputEntityId) {
    searchFilters.push({
      key: 'model_run_input_component_ids',
      operator: 'IS_ONE_OF',
      value: [filters.inputEntityId],
    });
  }

  return searchFilters;
}

function buildSort(sort: SortConfig): Array<{key: string; reverse: boolean}> {
  const sortKeyMap: Record<string, string> = {
    createdAt: 'created_at',
    modifiedAt: 'modified_at',
  };
  return [{key: sortKeyMap[sort.key], reverse: sort.direction === 'desc'}];
}

function mapListingToRow(id: string, listing: ModelRunListingData | undefined): ModelRunRow {
  if (!listing) {
    return {
      id,
      name: id,
      status: 'PENDING',
      modelName: '',
      creatorName: '',
      createdAt: '',
      modifiedAt: '',
      folderName: '',
    };
  }
  return {
    id: listing.id,
    name: listing.name ?? id,
    status: STATUS_CODE_TO_STATUS[listing.statusCode] ?? 'PENDING',
    modelName: listing.modelName ?? '',
    creatorName: listing.creator?.name ?? '',
    createdAt: listing.createdAt ?? '',
    modifiedAt: listing.modifiedAt ?? '',
    folderName: listing.folder?.name ?? '',
  };
}

interface UseModelRunSearchResult {
  runs: Array<ModelRunRow>;
  pagination: Pagination;
  isLoading: boolean;
  error: string | null;
  refresh: () => void;
}

export function useModelRunSearch(
  filters: ModelRunSearchFilters,
  sort: SortConfig,
  offset: number,
  limit: number
): UseModelRunSearchResult {
  const [runs, setRuns] = React.useState<Array<ModelRunRow>>([]);
  const [pagination, setPagination] = React.useState<Pagination>({offset: 0, limit, total: 0});
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [refreshCounter, setRefreshCounter] = React.useState(0);

  const refresh = React.useCallback(() => {
    setRefreshCounter((prev) => prev + 1);
  }, []);

  React.useEffect(() => {
    let cancelled = false;
    setIsLoading(true);
    setError(null);

    const searchParams = {
      offset,
      limit,
      query: filters.query,
      filters: buildSearchFilters(filters),
      sorts: buildSort(sort),
    };

    net
      .postJSONPromise({url: GLOBAL_SEARCH_URL, data: searchParams})
      .then((response: SearchResponse) => {
        if (cancelled) {
          return;
        }
        const modelRunsById = response.modelRunsById ?? {};
        const rows = response.ids.map((id) => mapListingToRow(id, modelRunsById[id]));
        setRuns(rows);
        setPagination({
          offset,
          limit,
          total: response.total,
          isExact: response.isExact,
        });
        setIsLoading(false);
      })
      .catch((err: unknown) => {
        if (cancelled) {
          return;
        }
        setError(err instanceof Error ? err.message : 'Search failed');
        setIsLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [filters, sort, offset, limit, refreshCounter]);

  return {runs, pagination, isLoading, error, refresh};
}
